import React, { useState } from 'react';
import { X, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/useI18n';

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { t } = useI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      await axios.post('/api/v1/auth/subscribe', { email });
      setIsSuccess(true);
      setTimeout(() => { onClose(); setEmail(''); setIsSuccess(false); }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to subscribe');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-playfair text-2xl font-bold text-primary-900">{t('newsletter.title')}</h2>
          <button onClick={onClose} className="text-primary-500 hover:text-primary-700 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="font-playfair text-xl font-bold text-primary-900 mb-2">✓</h3>
          </div>
        ) : (
          <>
            <p className="text-primary-700 mb-6">{t('newsletter.desc')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('newsletter.placeholder')} className="form-input pl-12" required />
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-primary-400" />
              </div>
              <button type="submit" disabled={isLoading} className="w-full btn-primary disabled:opacity-50">
                {isLoading ? '...' : t('newsletter.submit')}
              </button>
            </form>
            <p className="text-xs text-primary-500 mt-4 text-center">{t('newsletter.privacy')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterModal;